import dayjs from "dayjs";
import z from "zod";

import { PromotionTargetType, PromotionType } from "@/enums/promotion";
import { CreatePromotion, Promotion } from "@/types";
import {
  BUNDLE_TYPES,
  BUY_GET_TYPES,
  ORDER_PROMOTION_TYPES,
  PERCENT_TYPES,
} from "@/utils/promotion";

const DATE_TIME_INPUT_FORMAT = "YYYY-MM-DDTHH:mm";

const wholeCount = (label: string) =>
  z
    .number({ invalid_type_error: `${label} must be a number` })
    .int(`${label} must be a whole number`)
    .min(1, `${label} must be at least 1`)
    .nullable();

const aedAmount = (label: string) =>
  z
    .number({ invalid_type_error: `${label} must be a number` })
    .min(0, `${label} must be 0 or higher`)
    .refine(
      (amount) => Number(amount.toFixed(2)) === amount,
      `${label} takes at most 2 decimals`,
    )
    .nullable();

/** Mirrors the API's promotion rules for the fields the form shows for the chosen type. */
export const PROMOTION_FORM_SCHEMA = z
  .object({
    name: z.string().trim().min(1, "Name is required"),
    nameAr: z.string().trim().min(1, "Arabic name is required"),
    description: z.string().trim(),
    type: z.nativeEnum(PromotionType),
    value: aedAmount("Value"),
    buyQuantity: wholeCount("Buy quantity"),
    getQuantity: wholeCount("Get quantity"),
    maxApplicationsPerOrder: wholeCount("Max per order"),
    minimumSubtotal: aedAmount("Minimum subtotal"),
    targets: z.array(
      z.object({
        targetType: z.nativeEnum(PromotionTargetType),
        targetId: z.string(),
      }),
    ),
    code: z.string().trim().regex(/^\S*$/, "Codes cannot contain spaces"),
    maxRedemptions: wholeCount("Total redemptions"),
    maxRedemptionsPerUser: wholeCount("Redemptions per customer"),
    startsAt: z.string().min(1, "Pick a start date and time"),
    endsAt: z.string(),
    priority: z
      .number({ invalid_type_error: "Priority is required" })
      .int("Priority must be a whole number"),
    isActive: z.boolean(),
  })
  .superRefine((form, ctx) => {
    const issue = (path: Array<string | number>, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
    const { type } = form;

    if (type !== PromotionType.FREE_DELIVERY) {
      if (form.value === null) {
        issue(["value"], "Enter a value");
      } else if (
        PERCENT_TYPES.includes(type) &&
        (form.value <= 0 || form.value > 100)
      ) {
        issue(["value"], "Enter a percent above 0 and up to 100");
      } else if (!PERCENT_TYPES.includes(type) && form.value <= 0) {
        issue(["value"], "Enter an amount above 0");
      }
    }

    if (BUY_GET_TYPES.includes(type)) {
      if (form.buyQuantity === null) issue(["buyQuantity"], "Required");
      if (form.getQuantity === null) issue(["getQuantity"], "Required");
    }

    if (
      type === PromotionType.BUNDLE_PRICE &&
      (form.buyQuantity === null || form.buyQuantity < 2)
    ) {
      issue(["buyQuantity"], "A bundle needs at least 2 units");
    }

    if (!ORDER_PROMOTION_TYPES.includes(type)) {
      if (form.targets.length === 0) {
        issue(["targets"], "Add at least one target");
      }

      form.targets.forEach(({ targetType, targetId }, index) => {
        if (targetType !== PromotionTargetType.ALL && targetId === "") {
          issue(["targets", index, "targetId"], "Choose one");
        }
      });

      const keys = form.targets.map(
        ({ targetType, targetId }) => `${targetType}:${targetId}`,
      );

      if (new Set(keys).size !== keys.length) {
        issue(["targets"], "Each target can only be added once");
      }
    }

    if (form.code !== "" && form.endsAt === "") {
      issue(["endsAt"], "A promotion with a code needs an end date");
    }

    if (
      form.startsAt !== "" &&
      form.endsAt !== "" &&
      !dayjs(form.endsAt).isAfter(dayjs(form.startsAt))
    ) {
      issue(["endsAt"], "End must be after the start");
    }
  });

export type PromotionFormData = z.infer<typeof PROMOTION_FORM_SCHEMA>;

export const emptyPromotionForm = (): PromotionFormData => ({
  name: "",
  nameAr: "",
  description: "",
  type: PromotionType.PERCENTAGE_OFF,
  value: null,
  buyQuantity: null,
  getQuantity: null,
  maxApplicationsPerOrder: null,
  minimumSubtotal: null,
  targets: [{ targetType: PromotionTargetType.ALL, targetId: "" }],
  code: "",
  maxRedemptions: null,
  maxRedemptionsPerUser: null,
  startsAt: dayjs().format(DATE_TIME_INPUT_FORMAT),
  endsAt: "",
  priority: 0,
  isActive: true,
});

export const toPromotionFormValues = (
  promotion: Promotion,
): PromotionFormData => ({
  name: promotion.name,
  nameAr: promotion.nameAr,
  description: promotion.description ?? "",
  type: promotion.type,
  value: promotion.value,
  buyQuantity: promotion.buyQuantity,
  getQuantity: promotion.getQuantity,
  maxApplicationsPerOrder: promotion.maxApplicationsPerOrder,
  minimumSubtotal: promotion.minimumSubtotal,
  targets: promotion.targets.map(({ targetType, targetId }) => ({
    targetType,
    targetId: targetId ?? "",
  })),
  code: promotion.code ?? "",
  maxRedemptions: promotion.maxRedemptions,
  maxRedemptionsPerUser: promotion.maxRedemptionsPerUser,
  startsAt: dayjs(promotion.startsAt).format(DATE_TIME_INPUT_FORMAT),
  endsAt: promotion.endsAt
    ? dayjs(promotion.endsAt).format(DATE_TIME_INPUT_FORMAT)
    : "",
  priority: promotion.priority,
  isActive: promotion.isActive,
});

/** The API body for the form: fields the chosen type does not take are sent empty, so stale values never reach it. */
export const toPromotionPayload = (
  form: PromotionFormData,
): CreatePromotion => {
  const { type } = form;
  const isOrderLevel = ORDER_PROMOTION_TYPES.includes(type);
  const isBundle = BUNDLE_TYPES.includes(type);
  const code = form.code === "" ? null : form.code;

  return {
    name: form.name,
    nameAr: form.nameAr,
    description: form.description === "" ? null : form.description,
    type,
    value: type === PromotionType.FREE_DELIVERY ? 0 : (form.value ?? 0),
    buyQuantity: isBundle ? form.buyQuantity : null,
    getQuantity: BUY_GET_TYPES.includes(type) ? form.getQuantity : null,
    maxApplicationsPerOrder: isBundle ? form.maxApplicationsPerOrder : null,
    minimumSubtotal: isOrderLevel ? form.minimumSubtotal : null,
    targets: isOrderLevel
      ? []
      : form.targets.map(({ targetType, targetId }) => ({
          targetType,
          targetId: targetType === PromotionTargetType.ALL ? null : targetId,
        })),
    code,
    maxRedemptions: code === null ? null : form.maxRedemptions,
    maxRedemptionsPerUser: code === null ? null : form.maxRedemptionsPerUser,
    startsAt: dayjs(form.startsAt).toISOString(),
    endsAt: form.endsAt === "" ? null : dayjs(form.endsAt).toISOString(),
    priority: form.priority,
    isActive: form.isActive,
  };
};
